import { Link, createFileRoute } from '@tanstack/react-router'
import { useAdminStats, useLibrary } from '../lib/admin-api'

export const Route = createFileRoute('/admin/')({
  component: OverviewPage,
})

function OverviewPage() {
  const stats = useAdminStats()
  const library = useLibrary()

  const outOfStock = (library.data ?? []).filter((game) => game.availableKeys === 0)

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Overview</h1>

      <dl className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Games" value={stats.data?.games} />
        <Stat label="Keys total" value={stats.data?.totalKeys} />
        <Stat label="Available" value={stats.data?.available} tone="good" />
        <Stat label="Claimed" value={stats.data?.claimed} />
        <Stat label="Expiring ≤30d" value={stats.data?.expiringSoon} tone="warn" />
      </dl>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-line bg-surface-1 p-6">
          <h2 className="font-display text-base font-semibold tracking-tight">Add more keys</h2>
          <p className="mt-1 text-sm text-ink-soft">
            Search a game, pick it, paste the codes. Metadata and artwork are fetched for you.
          </p>
          <Link
            to="/admin/add"
            className="mt-4 inline-block rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-surface-0 transition-colors hover:bg-accent-strong"
          >
            Add keys
          </Link>
        </section>

        <section className="rounded-xl border border-line bg-surface-1 p-6">
          <h2 className="font-display text-base font-semibold tracking-tight">Out of stock</h2>
          {library.isPending ? (
            <p className="mt-2 text-sm text-ink-muted">Loading…</p>
          ) : outOfStock.length === 0 ? (
            <p className="mt-2 text-sm text-ink-soft">
              Everything in the library still has at least one key.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-ink-soft">
                {outOfStock.length} {outOfStock.length === 1 ? 'game has' : 'games have'} no keys
                left, so {outOfStock.length === 1 ? 'it is' : 'they are'} hidden from the catalog.
              </p>
              <ul className="mt-3 space-y-1">
                {outOfStock.slice(0, 6).map((game) => (
                  <li key={game.id} className="truncate text-sm text-ink-muted">
                    {game.title}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: number | undefined
  tone?: 'default' | 'good' | 'warn'
}) {
  const toneClass =
    tone === 'good' ? 'text-good' : tone === 'warn' ? 'text-warn' : 'text-ink'

  return (
    <div className="rounded-xl border border-line bg-surface-1 px-4 py-3.5">
      <dt className="text-[11px] tracking-wider text-ink-muted uppercase">{label}</dt>
      <dd className={`mt-1 font-mono text-2xl font-medium tabular-nums ${toneClass}`}>
        {value ?? '—'}
      </dd>
    </div>
  )
}
